import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Building2,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Package,
  Users,
  Calendar,
  ShoppingCart,
  MoreVertical,
  Ban,
  Pause,
  Play,
  CalendarPlus,
  Power,
  Info,
  X,
  Settings
} from 'lucide-react'
import { adminApi, AdminOrgLicense, OrgLicenseSubscriptionStatus, type OrganizationSimple, type AdminProduct } from '@/api/admin'
import { handleApiError } from '@/api/client'

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const formatDateTime = (dateStr: string | null) => {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleString('ko-KR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// Helper to get subscription status with fallback
const getSubscriptionStatus = (license: AdminOrgLicense): OrgLicenseSubscriptionStatus => {
  if (license.subscriptionStatus) {
    return license.subscriptionStatus
  }
  // Fallback: infer from isActive and validUntil
  if (!license.isActive) {
    return 'terminated'
  }
  if (license.validUntil && new Date(license.validUntil) < new Date()) {
    return 'expired'
  }
  return 'active'
}

// Status badge component
const StatusBadge = ({ status }: { status: OrgLicenseSubscriptionStatus }) => {
  const styles: Record<OrgLicenseSubscriptionStatus, string> = {
    active: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-700',
    suspended: 'bg-yellow-100 text-yellow-700',
    expired: 'bg-gray-100 text-gray-700',
    terminated: 'bg-slate-200 text-slate-700',
  }

  const labels: Record<OrgLicenseSubscriptionStatus, string> = {
    active: '활성',
    cancelled: '취소됨',
    suspended: '정지됨',
    expired: '만료됨',
    terminated: '종료됨',
  }

  const icons: Record<OrgLicenseSubscriptionStatus, React.ReactNode> = {
    active: <CheckCircle2 className="w-3 h-3" />,
    cancelled: <XCircle className="w-3 h-3" />,
    suspended: <Pause className="w-3 h-3" />,
    expired: <Calendar className="w-3 h-3" />,
    terminated: <Power className="w-3 h-3" />,
  }

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${styles[status] || styles.active}`}>
      {icons[status] || icons.active}
      {labels[status] || labels.active}
    </span>
  )
}

// Action dropdown component
const ActionDropdown = ({
  license,
  onAction,
}: {
  license: AdminOrgLicense,
  onAction: (action: string, license: AdminOrgLicense) => void
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const status = getSubscriptionStatus(license)

  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const actions: { id: string; label: string; icon: React.ElementType; danger?: boolean }[] = []

  // 좌석 조정은 항상 가능
  actions.push({ id: 'seats', label: '좌석 조정', icon: Users })

  // 상태에 따른 액션
  if (status === 'active') {
    actions.push({ id: 'extend', label: '구독 연장', icon: CalendarPlus })
    actions.push({ id: 'suspend', label: '구독 정지', icon: Pause })
    actions.push({ id: 'cancel', label: '구독 취소', icon: Ban })
    actions.push({ id: 'terminate', label: '구독 종료', icon: Power, danger: true })
  } else if (status === 'suspended') {
    actions.push({ id: 'restart', label: '구독 재시작', icon: Play })
    actions.push({ id: 'terminate', label: '구독 종료', icon: Power, danger: true })
  } else if (status === 'cancelled' || status === 'expired') {
    actions.push({ id: 'restart', label: '구독 재시작', icon: Play })
  }
  // terminated 상태는 복구 불가하므로 추가 액션 없음

  // 상세보기
  actions.push({ id: 'details', label: '상세 정보', icon: Info })

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={buttonRef}
        onClick={() => {
          if (!isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect()
            const menuHeight = actions.length * 36 + 8
            const dropUp = rect.bottom + menuHeight > window.innerHeight
            setMenuPos({
              top: dropUp ? rect.top - menuHeight : rect.bottom + 4,
              left: Math.max(8, rect.right - 192),
            })
          }
          setIsOpen(!isOpen)
        }}
        className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
      >
        <MoreVertical className="w-4 h-4 text-slate-500" />
      </button>

      {isOpen && menuPos && (
        <div
          className="fixed z-50 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1"
          style={{ top: menuPos.top, left: menuPos.left }}
        >
          {actions.map((action) => (
            <button
              key={action.id}
              onClick={() => {
                onAction(action.id, license)
                setIsOpen(false)
              }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-slate-50 ${
                action.danger ? 'text-red-600 hover:bg-red-50' : 'text-slate-700'
              }`}
            >
              <action.icon className="w-4 h-4" />
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// Modal base component
const Modal = ({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
}) => {
  if (!isOpen) return null

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div
        className="relative z-10 bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-4 top-4 p-1 hover:bg-slate-100 rounded-lg"
        >
          <X className="w-5 h-5 text-slate-500" />
        </button>
        <h2 className="text-lg font-semibold text-slate-900 mb-4">{title}</h2>
        {children}
      </div>
    </div>,
    document.body
  )
}

// Seats adjustment modal
const SeatsModal = ({
  isOpen,
  onClose,
  license,
  onSubmit
}: {
  isOpen: boolean
  onClose: () => void
  license: AdminOrgLicense | null
  onSubmit: (seats: number) => void
}) => {
  const [seats, setSeats] = useState('')

  // license 변경 시 seats 상태 동기화
  useEffect(() => {
    if (license) {
      setSeats(String(license.purchasedSeats))
    }
  }, [license])

  if (!license) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="좌석 수 조정">
      <div className="space-y-4">
        <div className="bg-slate-50 rounded-lg p-3 space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">현재 최대 좌석 (max_seats)</span>
            <span className="font-semibold text-slate-900">
              {license.purchasedSeats === 0 ? '무제한 (0)' : `${license.purchasedSeats}석`}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">사용 중</span>
            <span className="font-medium text-slate-700">{license.usedSeats}석</span>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            새 좌석 수
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={seats}
            onChange={(e) => {
              const val = e.target.value
              if (val === '' || /^\d+$/.test(val)) {
                setSeats(val)
              }
            }}
            placeholder="0 = 무제한"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          />
          <p className="text-xs text-slate-500 mt-1">0 = 무제한</p>
        </div>
        <div className="flex gap-2 pt-2">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
          >
            취소
          </button>
          <button
            onClick={() => onSubmit(seats === '' ? 0 : parseInt(seats))}
            className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
          >
            저장
          </button>
        </div>
      </div>
    </Modal>
  )
}

// Reason input modal (for cancel, suspend)
const ReasonModal = ({
  isOpen,
  onClose,
  title,
  description,
  buttonText,
  buttonColor = 'amber',
  onSubmit
}: {
  isOpen: boolean
  onClose: () => void
  title: string
  description: string
  buttonText: string
  buttonColor?: 'amber' | 'red'
  onSubmit: (reason: string) => void
}) => {
  const [reason, setReason] = useState('')

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="space-y-4">
        <p className="text-sm text-slate-600">{description}</p>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            사유 <span className="text-red-500">*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="사유를 입력해주세요..."
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          />
        </div>
        <div className="flex gap-2 pt-2">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
          >
            취소
          </button>
          <button
            onClick={() => {
              if (reason.trim()) {
                onSubmit(reason)
                setReason('')
              }
            }}
            disabled={!reason.trim()}
            className={`flex-1 px-4 py-2 text-white rounded-lg disabled:opacity-50 ${
              buttonColor === 'red' ? 'bg-red-500 hover:bg-red-600' : 'bg-amber-500 hover:bg-amber-600'
            }`}
          >
            {buttonText}
          </button>
        </div>
      </div>
    </Modal>
  )
}

// Date select modal (for extend, restart)
const DateSelectModal = ({
  isOpen,
  onClose,
  title,
  description,
  currentDate,
  isOptional = false,
  onSubmit
}: {
  isOpen: boolean
  onClose: () => void
  title: string
  description: string
  currentDate: string | null
  isOptional?: boolean
  onSubmit: (date: string | undefined) => void
}) => {
  const [date, setDate] = useState(currentDate || '')

  // 모달이 열릴 때 currentDate로 동기화
  useEffect(() => {
    if (isOpen) {
      setDate(currentDate || '')
    }
  }, [isOpen, currentDate])

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="space-y-4">
        <p className="text-sm text-slate-600">{description}</p>
        {currentDate && (
          <p className="text-sm text-slate-500">
            현재 만료일: <span className="font-medium">{formatDate(currentDate)}</span>
          </p>
        )}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            {isOptional ? '새 만료일 (선택사항)' : '새 만료일'}
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            min={currentDate || undefined}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          />
        </div>
        <div className="flex gap-2 pt-2">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
          >
            취소
          </button>
          <button
            onClick={() => onSubmit(date || undefined)}
            disabled={!isOptional && !date}
            className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50"
          >
            확인
          </button>
        </div>
      </div>
    </Modal>
  )
}

// Terminate confirmation modal
const TerminateModal = ({
  isOpen,
  onClose,
  license,
  onSubmit
}: {
  isOpen: boolean
  onClose: () => void
  license: AdminOrgLicense | null
  onSubmit: (reason?: string) => void
}) => {
  const [reason, setReason] = useState('')

  if (!license) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="구독 종료">
      <div className="space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800 font-medium">경고</p>
          <p className="text-sm text-red-700 mt-1">
            구독을 종료하면 복구할 수 없습니다. 정말 종료하시겠습니까?
          </p>
        </div>
        <div>
          <p className="text-sm text-slate-600">
            조직: <span className="font-medium">{license.organizationName}</span>
          </p>
          <p className="text-sm text-slate-600">
            상품: <span className="font-medium">{license.productName}</span>
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            종료 사유 (선택)
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="사유를 입력해주세요..."
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          />
        </div>
        <div className="flex gap-2 pt-2">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
          >
            취소
          </button>
          <button
            onClick={() => onSubmit(reason || undefined)}
            className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
          >
            종료
          </button>
        </div>
      </div>
    </Modal>
  )
}

// Details modal
const DetailsModal = ({
  isOpen,
  onClose,
  license
}: {
  isOpen: boolean
  onClose: () => void
  license: AdminOrgLicense | null
}) => {
  if (!license) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="구독 상세 정보">
      <div className="space-y-4 max-h-[60vh] overflow-y-auto">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-slate-500">조직</p>
            <p className="text-sm font-medium">{license.organizationName || '-'}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">상품</p>
            <p className="text-sm font-medium">{license.productName || '-'}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">상태</p>
            <StatusBadge status={getSubscriptionStatus(license)} />
          </div>
          <div>
            <p className="text-xs text-slate-500">좌석</p>
            <p className="text-sm font-medium">
              {license.usedSeats} / {license.purchasedSeats === 0 ? '∞' : license.purchasedSeats}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500">유효기간</p>
            <p className="text-sm font-medium">
              {formatDate(license.validFrom)} ~ {license.validUntil ? formatDate(license.validUntil) : '무기한'}
            </p>
          </div>
          {license.originalValidUntil && (
            <div>
              <p className="text-xs text-slate-500">원래 만료일</p>
              <p className="text-sm font-medium">{formatDate(license.originalValidUntil)}</p>
            </div>
          )}
        </div>

        {license.cancellationReason && (
          <div className="border-t pt-4">
            <p className="text-xs text-slate-500 mb-1">취소 사유</p>
            <p className="text-sm bg-red-50 p-2 rounded-lg text-red-700">{license.cancellationReason}</p>
            <p className="text-xs text-slate-400 mt-1">취소일: {formatDateTime(license.cancelledAt)}</p>
          </div>
        )}

        {license.suspensionReason && (
          <div className="border-t pt-4">
            <p className="text-xs text-slate-500 mb-1">정지 사유</p>
            <p className="text-sm bg-yellow-50 p-2 rounded-lg text-yellow-700">{license.suspensionReason}</p>
            <p className="text-xs text-slate-400 mt-1">정지일: {formatDateTime(license.suspendedAt)}</p>
          </div>
        )}

        {license.terminationReason && (
          <div className="border-t pt-4">
            <p className="text-xs text-slate-500 mb-1">종료 사유</p>
            <p className="text-sm bg-slate-100 p-2 rounded-lg text-slate-700">{license.terminationReason}</p>
            <p className="text-xs text-slate-400 mt-1">종료일: {formatDateTime(license.terminatedAt)}</p>
          </div>
        )}

        {license.restartedAt && (
          <div className="border-t pt-4">
            <p className="text-xs text-slate-500 mb-1">마지막 재시작</p>
            <p className="text-sm">{formatDateTime(license.restartedAt)}</p>
          </div>
        )}

        <div className="border-t pt-4">
          <p className="text-xs text-slate-500 mb-1">생성일</p>
          <p className="text-sm">{formatDateTime(license.createdAt)}</p>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t">
        <button
          onClick={onClose}
          className="w-full px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
        >
          닫기
        </button>
      </div>
    </Modal>
  )
}

export function OrgSubscriptionManagement() {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(0)
  const [filterStatus, setFilterStatus] = useState<OrgLicenseSubscriptionStatus | undefined>(undefined)
  const [filterPurchased, setFilterPurchased] = useState<boolean | undefined>(undefined)
  const [filterOrganizationId, setFilterOrganizationId] = useState<number | undefined>(undefined)
  const [filterProductId, setFilterProductId] = useState<number | undefined>(undefined)

  // Modal states
  const [seatsModal, setSeatsModal] = useState<{ isOpen: boolean; license: AdminOrgLicense | null }>({
    isOpen: false, license: null
  })
  const [cancelModal, setCancelModal] = useState<{ isOpen: boolean; license: AdminOrgLicense | null }>({
    isOpen: false, license: null
  })
  const [suspendModal, setSuspendModal] = useState<{ isOpen: boolean; license: AdminOrgLicense | null }>({
    isOpen: false, license: null
  })
  const [restartModal, setRestartModal] = useState<{ isOpen: boolean; license: AdminOrgLicense | null }>({
    isOpen: false, license: null
  })
  const [extendModal, setExtendModal] = useState<{ isOpen: boolean; license: AdminOrgLicense | null }>({
    isOpen: false, license: null
  })
  const [terminateModal, setTerminateModal] = useState<{ isOpen: boolean; license: AdminOrgLicense | null }>({
    isOpen: false, license: null
  })
  const [detailsModal, setDetailsModal] = useState<{ isOpen: boolean; license: AdminOrgLicense | null }>({
    isOpen: false, license: null
  })

  // 필터용 조직/상품 목록 조회
  const { data: organizations } = useQuery({
    queryKey: ['admin', 'all-organizations'],
    queryFn: () => adminApi.getAllOrganizations(),
    staleTime: 5 * 60 * 1000,
  })

  const { data: productsData } = useQuery({
    queryKey: ['admin', 'all-products-for-filter'],
    queryFn: () => adminApi.getProducts({ size: 200 }),
    staleTime: 5 * 60 * 1000,
  })

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'org-licenses', { page, filterStatus, filterPurchased, filterOrganizationId, filterProductId }],
    queryFn: () => adminApi.getOrgLicenses({
      page,
      size: 20,
      subscriptionStatus: filterStatus,
      isPurchased: filterPurchased,
      organizationId: filterOrganizationId,
      productId: filterProductId,
    }),
    staleTime: 30 * 1000,
  })

  // Toast state for feedback
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  // Mutations
  const updateSeatsMutation = useMutation({
    mutationFn: ({ id, seats }: { id: number; seats: number }) =>
      adminApi.updateOrgLicenseSeats(id, { purchasedSeats: seats }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'org-licenses'] })
      setSeatsModal({ isOpen: false, license: null })
      showToast('좌석 수가 변경되었습니다.')
    },
    onError: (err: unknown) => {
      showToast(`좌석 변경 실패: ${handleApiError(err)}`, 'error')
    }
  })

  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      adminApi.cancelOrgLicense(id, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'org-licenses'] })
      setCancelModal({ isOpen: false, license: null })
      showToast('구독이 취소되었습니다.')
    },
    onError: (err: unknown) => {
      showToast(`구독 취소 실패: ${handleApiError(err)}`, 'error')
    }
  })

  const suspendMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      adminApi.suspendOrgLicense(id, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'org-licenses'] })
      setSuspendModal({ isOpen: false, license: null })
      showToast('구독이 정지되었습니다.')
    },
    onError: (err: unknown) => {
      showToast(`구독 정지 실패: ${handleApiError(err)}`, 'error')
    }
  })

  const restartMutation = useMutation({
    mutationFn: ({ id, newValidUntil }: { id: number; newValidUntil?: string }) =>
      adminApi.restartOrgLicense(id, { newValidUntil }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'org-licenses'] })
      setRestartModal({ isOpen: false, license: null })
      showToast('구독이 재시작되었습니다.')
    },
    onError: (err: unknown) => {
      showToast(`구독 재시작 실패: ${handleApiError(err)}`, 'error')
    }
  })

  const extendMutation = useMutation({
    mutationFn: ({ id, newValidUntil }: { id: number; newValidUntil: string }) =>
      adminApi.extendOrgLicense(id, { newValidUntil }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'org-licenses'] })
      setExtendModal({ isOpen: false, license: null })
      showToast('구독이 연장되었습니다.')
    },
    onError: (err: unknown) => {
      showToast(`구독 연장 실패: ${handleApiError(err)}`, 'error')
    }
  })

  const terminateMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) =>
      adminApi.terminateOrgLicense(id, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'org-licenses'] })
      setTerminateModal({ isOpen: false, license: null })
      showToast('구독이 종료되었습니다.')
    },
    onError: (err: unknown) => {
      showToast(`구독 종료 실패: ${handleApiError(err)}`, 'error')
    }
  })

  const handleAction = (action: string, license: AdminOrgLicense) => {
    switch (action) {
      case 'seats':
        setSeatsModal({ isOpen: true, license })
        break
      case 'cancel':
        setCancelModal({ isOpen: true, license })
        break
      case 'suspend':
        setSuspendModal({ isOpen: true, license })
        break
      case 'restart':
        setRestartModal({ isOpen: true, license })
        break
      case 'extend':
        setExtendModal({ isOpen: true, license })
        break
      case 'terminate':
        setTerminateModal({ isOpen: true, license })
        break
      case 'details':
        setDetailsModal({ isOpen: true, license })
        break
    }
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-10 h-10 text-slate-600 animate-spin mb-4" />
        <p className="text-slate-600">조직 구독 목록을 불러오는 중...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-slate-900 font-medium mb-2">데이터를 불러올 수 없습니다</p>
        <p className="text-slate-500 text-sm">{(error as Error).message}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Building2 className="w-7 h-7 text-amber-600" />
            조직 구독 관리
          </h1>
          <p className="text-slate-600 mt-1">총 {data?.totalElements ?? 0}건의 조직 라이선스</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
        {/* 1줄: 상태 + 구매 */}
        <div className="flex flex-wrap gap-3 items-center">
          {/* 상태 필터 */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-500">상태:</span>
            <div className="flex gap-1">
              {[
                { label: '전체', value: undefined },
                { label: '활성', value: 'active' as OrgLicenseSubscriptionStatus },
                { label: '정지', value: 'suspended' as OrgLicenseSubscriptionStatus },
                { label: '취소', value: 'cancelled' as OrgLicenseSubscriptionStatus },
                { label: '만료', value: 'expired' as OrgLicenseSubscriptionStatus },
                { label: '종료', value: 'terminated' as OrgLicenseSubscriptionStatus },
              ].map((opt) => (
                <button
                  key={String(opt.value)}
                  onClick={() => { setFilterStatus(opt.value); setPage(0) }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    filterStatus === opt.value
                      ? 'bg-amber-500 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* 구매 필터 */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-500">구매:</span>
            <div className="flex gap-1">
              {[
                { label: '전체', value: undefined },
                { label: '구매완료', value: true },
                { label: '미구매(Open)', value: false },
              ].map((opt) => (
                <button
                  key={String(opt.value)}
                  onClick={() => { setFilterPurchased(opt.value); setPage(0) }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    filterPurchased === opt.value
                      ? 'bg-amber-500 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 2줄: 회사 + 상품 */}
        <div className="flex flex-wrap gap-3 items-center">
          {/* 회사 필터 */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-500">회사:</span>
            <select
              value={filterOrganizationId ?? ''}
              onChange={(e) => {
                setFilterOrganizationId(e.target.value ? Number(e.target.value) : undefined)
                setPage(0)
              }}
              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 bg-white text-slate-700 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 min-w-[140px]"
            >
              <option value="">전체 회사</option>
              {organizations?.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.displayName || org.name}{org.subdomain ? ` (${org.subdomain})` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* 상품 필터 */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-500">상품:</span>
            <select
              value={filterProductId ?? ''}
              onChange={(e) => {
                setFilterProductId(e.target.value ? Number(e.target.value) : undefined)
                setPage(0)
              }}
              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 bg-white text-slate-700 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 min-w-[140px]"
            >
              <option value="">전체 상품</option>
              {productsData?.content.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-visible">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase rounded-tl-xl">ID</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">조직</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">상품</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase">구매</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase">상태</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase">좌석</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">유효기간</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">구매일</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap rounded-tr-xl"><span className="inline-flex items-center gap-1"><Settings className="w-3.5 h-3.5" />액션</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data?.content.map((license) => (
                <tr key={license.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-sm text-slate-600">{license.id}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-slate-400" />
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          {license.organizationName || `Org #${license.organizationId}`}
                        </p>
                        {license.organizationSubdomain && (
                          <p className="text-xs text-slate-500">{license.organizationSubdomain}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-900">
                        {license.productName || `Product #${license.productId}`}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {license.purchasedAt ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                        <ShoppingCart className="w-3 h-3" />
                        구매완료
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                        Open
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <StatusBadge status={getSubscriptionStatus(license)} />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1 text-sm">
                      <Users className="w-3.5 h-3.5 text-slate-400" />
                      <span className="text-slate-900 font-medium">{license.usedSeats}</span>
                      <span className="text-slate-400">/</span>
                      <span className="text-slate-600">
                        {license.purchasedSeats === 0 ? '∞' : license.purchasedSeats}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 text-sm text-slate-600">
                      <Calendar className="w-3.5 h-3.5 text-slate-400" />
                      <span>{formatDate(license.validFrom)}</span>
                      <span className="text-slate-400">-</span>
                      <span>{license.validUntil ? formatDate(license.validUntil) : '무기한'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-500">
                    {formatDateTime(license.purchasedAt)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <ActionDropdown license={license} onAction={handleAction} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {data?.content.length === 0 && (
          <div className="text-center py-12">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">조직 라이선스가 없습니다</p>
          </div>
        )}

        {/* Pagination */}
        {data && data.totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
            <p className="text-sm text-slate-600">
              {data.page * data.size + 1} - {Math.min((data.page + 1) * data.size, data.totalElements)} / {data.totalElements}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={data.first}
                className="p-2 hover:bg-slate-100 rounded-lg disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm text-slate-600">
                {data.page + 1} / {data.totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={data.last}
                className="p-2 hover:bg-slate-100 rounded-lg disabled:opacity-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <SeatsModal
        isOpen={seatsModal.isOpen}
        onClose={() => setSeatsModal({ isOpen: false, license: null })}
        license={seatsModal.license}
        onSubmit={(seats) => {
          if (seatsModal.license) {
            updateSeatsMutation.mutate({ id: seatsModal.license.id, seats })
          }
        }}
      />

      <ReasonModal
        isOpen={cancelModal.isOpen}
        onClose={() => setCancelModal({ isOpen: false, license: null })}
        title="구독 취소"
        description="구독을 취소하면 즉시 비활성화됩니다. 나중에 재시작할 수 있습니다."
        buttonText="취소 처리"
        buttonColor="red"
        onSubmit={(reason) => {
          if (cancelModal.license) {
            cancelMutation.mutate({ id: cancelModal.license.id, reason })
          }
        }}
      />

      <ReasonModal
        isOpen={suspendModal.isOpen}
        onClose={() => setSuspendModal({ isOpen: false, license: null })}
        title="구독 정지"
        description="구독을 일시 정지합니다. 언제든지 재시작할 수 있습니다."
        buttonText="정지"
        buttonColor="amber"
        onSubmit={(reason) => {
          if (suspendModal.license) {
            suspendMutation.mutate({ id: suspendModal.license.id, reason })
          }
        }}
      />

      <DateSelectModal
        isOpen={restartModal.isOpen}
        onClose={() => setRestartModal({ isOpen: false, license: null })}
        title="구독 재시작"
        description="구독을 다시 활성화합니다. 필요한 경우 새 만료일을 지정할 수 있습니다."
        currentDate={restartModal.license?.validUntil ?? null}
        isOptional={true}
        onSubmit={(date) => {
          if (restartModal.license) {
            restartMutation.mutate({ id: restartModal.license.id, newValidUntil: date })
          }
        }}
      />

      <DateSelectModal
        isOpen={extendModal.isOpen}
        onClose={() => setExtendModal({ isOpen: false, license: null })}
        title="구독 연장"
        description="구독 만료일을 연장합니다."
        currentDate={extendModal.license?.validUntil ?? null}
        isOptional={false}
        onSubmit={(date) => {
          if (extendModal.license && date) {
            extendMutation.mutate({ id: extendModal.license.id, newValidUntil: date })
          }
        }}
      />

      <TerminateModal
        isOpen={terminateModal.isOpen}
        onClose={() => setTerminateModal({ isOpen: false, license: null })}
        license={terminateModal.license}
        onSubmit={(reason) => {
          if (terminateModal.license) {
            terminateMutation.mutate({ id: terminateModal.license.id, reason })
          }
        }}
      />

      <DetailsModal
        isOpen={detailsModal.isOpen}
        onClose={() => setDetailsModal({ isOpen: false, license: null })}
        license={detailsModal.license}
      />

      {/* Toast notification */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium text-white transition-all ${
          toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
        }`}>
          {toast.message}
        </div>
      )}
    </div>
  )
}
